import { transitionWindow, transitionEase, interpolateCameraValue, type IncomingTransition } from './camera-transitions.js';
export { transitionWindow } from './camera-transitions.js';
/** Independent film-time camera tracks shared by Studio and capture. */
export const CAMERA_TRACKS = ['rotation', 'tilt', 'roll', 'altitude', 'lens'] as const;
export type CameraTrack = typeof CAMERA_TRACKS[number];
export interface CameraKeyframe extends IncomingTransition { id?: string | undefined; seconds: number; value: number; automatic?: boolean | undefined }
export interface CameraKeyframes {
  version: 1 | 2;
  targetLock: boolean;
  rotation: CameraKeyframe[];
  tilt: CameraKeyframe[];
  /** Absent in projects saved before Bank was introduced. */
  roll?: CameraKeyframe[] | undefined;
  altitude: CameraKeyframe[];
  lens: CameraKeyframe[];
}
export const CAMERA_RENDERED_TILT_LIMIT = 85;
export const CAMERA_TRACK_LIMITS = {
  rotation: { label: 'Rotation', unit: '°', min: 0, max: 360, step: .1 },
  tilt: { label: 'Tilt', unit: '°', min: -90, max: 90, step: .1 },
  roll: { label: 'Bank', unit: '°', min: -180, max: 180, step: .1 },
  altitude: { label: 'Altitude', unit: 'm AGL', min: 10, max: 20000, step: 1 },
  lens: { label: 'Lens', unit: 'mm', min: 24, max: 800, step: 1 },
} as const;

export function resolveCameraKeyframes(input: unknown, duration: number): CameraKeyframes {
  if (!Number.isFinite(duration) || duration <= 0) throw new RangeError('Keyframe duration must be positive.');
  const candidate = input as CameraKeyframes | undefined;
  if (!candidate || ![1, 2].includes(candidate.version) || typeof candidate.targetLock !== 'boolean') throw new RangeError('Invalid camera track settings.');
  const result = { version: candidate.version, targetLock: candidate.targetLock } as CameraKeyframes;
  for (const axis of CAMERA_TRACKS) {
    const keys = candidate[axis] ?? (candidate.version === 2 ? [] : axis === 'roll' ? [{ seconds: 0, value: 0, easing: 'smooth' }] : undefined), limits = CAMERA_TRACK_LIMITS[axis];
    if (!Array.isArray(keys) || (candidate.version === 1 && keys.length < 1) || keys.length > 200) throw new RangeError(`${axis} needs from 1 through 200 keys.`);
    const sorted = keys.map(key => {
      if (!key || !Number.isFinite(key.seconds) || key.seconds < 0 || key.seconds > duration
        || !Number.isFinite(key.value) || key.value < limits.min || key.value > limits.max
        || !['linear', 'smooth'].includes(key.easing)
        || (key.automatic !== undefined && (candidate.version !== 2 || typeof key.automatic !== 'boolean'))) throw new RangeError(`Invalid ${axis} keyframe.`);
      if (candidate.version === 2 && (!key.id || key.id.length > 64 || !['cut', 'transition'].includes(key.transition ?? 'transition')
        || (key.durationSeconds !== undefined && key.durationSeconds !== 'interval' && (!Number.isFinite(key.durationSeconds) || key.durationSeconds < .1 || key.durationSeconds > 30)))) throw new RangeError(`Invalid ${axis} transition.`);
      return { ...key };
    }).sort((a, b) => a.seconds - b.seconds);
    if (sorted.some((key, i) => i > 0 && key.seconds - sorted[i - 1]!.seconds < .001)) throw new RangeError(`${axis} key times must be distinct.`);
    if (candidate.version === 2 && new Set(sorted.map(key => key.id)).size !== sorted.length) throw new RangeError(`${axis} key identities must be distinct.`);
    result[axis] = sorted;
  }
  if (new TextEncoder().encode(JSON.stringify(result)).length > 20 * 1024) throw new RangeError('Camera animation is too large. Remove some keys before exporting.');
  return result;
}

export function evaluateCameraTrack(keys: readonly CameraKeyframe[], seconds: number, rotation = false): number {
  if (!Number.isFinite(seconds)) throw new RangeError('Keyframe time must be finite.');
  const exact = keys.find(key => Math.abs(key.seconds - seconds) < 1e-9);
  if (exact) return exact.value;
  if (seconds <= keys[0]!.seconds) return keys[0]!.value;
  const index = keys.findLastIndex(key => key.seconds <= seconds), start = keys[index]!, end = keys[index + 1];
  if (!end) return start.value;
  const t = (seconds - start.seconds) / (end.seconds - start.seconds);
  const amount = start.easing === 'smooth' ? t * t * t * (t * (t * 6 - 15) + 10) : t;
  let delta = end.value - start.value;
  if (rotation && Math.abs(delta) < 360) delta = ((delta + 540) % 360) - 180;
  const value = start.value + delta * amount;
  return rotation && (value < 0 || value > 360) ? ((value % 360) + 360) % 360 : value;
}
export function evaluateCameraKeyframes(tracks: CameraKeyframes, seconds: number) {
  return { rotation: evaluateCameraTrack(tracks.rotation, seconds, true), tilt: evaluateCameraTrack(tracks.tilt, seconds),
    roll: tracks.roll?.length ? evaluateCameraTrack(tracks.roll, seconds) : 0,
    altitude: evaluateCameraTrack(tracks.altitude, seconds), lens: evaluateCameraTrack(tracks.lens, seconds) };
}
export function setCameraKeyframe<T extends CameraKeyframes>(tracks: T, axis: CameraTrack, key: CameraKeyframe,
  duration: number, previousTime = key.seconds): T {
  return resolveCameraKeyframes({ ...tracks, [axis]: [...(tracks[axis] ?? []).filter(item => Math.abs(item.seconds - previousTime) >= .001), key] }, duration) as T;
}
export function retimeCameraKeyframes(tracks: CameraKeyframes, from: number, to: number): CameraKeyframes {
  const result = resolveCameraKeyframes(tracks, from);
  for (const axis of CAMERA_TRACKS) result[axis] = (result[axis] ?? []).map(key => ({ ...key, seconds: key.seconds * to / from }));
  return resolveCameraKeyframes(result, to);
}


export const emptyCameraKeyframes = (): CameraKeyframes => ({ version: 2, targetLock: true, rotation: [], tilt: [], roll: [], altitude: [], lens: [] });
export const hasCameraKeys = (tracks?: CameraKeyframes) => !!tracks && CAMERA_TRACKS.some(axis => (tracks[axis]?.length ?? 0) > 0);

/** Undefined means the rig remains automatic on this axis at this time. */
export function evaluateAuthoredCameraTrack(tracks: CameraKeyframes, axis: CameraTrack, seconds: number,
  automaticAt: (seconds: number) => number): number | undefined {
  const keys = tracks[axis] ?? [];
  if (!keys.length) return undefined;
  if (tracks.version === 1) return evaluateCameraTrack(keys, seconds, axis === 'rotation');
  const nextIndex = keys.findIndex(key => key.seconds > seconds + 1e-9);
  if (nextIndex < 0) return keys.at(-1)!.automatic ? undefined : keys.at(-1)!.value;
  const next = keys[nextIndex]!, previous = keys[nextIndex - 1];
  const window = transitionWindow(next, previous?.seconds ?? 0);
  if (seconds < window.start || window.duration === 0) return previous?.automatic ? undefined : previous?.value;
  return interpolateCameraValue(previous?.automatic ? automaticAt(seconds) : previous?.value ?? automaticAt(window.start),
    next.automatic ? automaticAt(seconds) : next.value,
    transitionEase((seconds - window.start) / window.duration, next.easing), axis === 'rotation');
}
